import { FlyToInterpolator } from '@deck.gl/core/typed';

import { useViewState } from 'components/maps';
import { getScaledDuration } from 'components/maps/BaseMap/utils';
import { SetStateAction, useCallback, useContext, useMemo } from 'react';
import type { LngLatLike, Point, ViewState } from 'react-map-gl/maplibre';
import { DeckTransitionProps, KineticMapContext } from '../MapContext';
import { LngLatBounds } from 'maplibre-gl';

const DEFAULT_DURATION = 2000;

/**
 * Project a lnglat map to the containers pixel coordinates.
 * WARNING Causes re-render when the viewState changes.
 *
 * @param lnglat  The lnglat to project to a point
 * @returns  The pixel coordinates of the lnglat
 */
export const useKineticMapLiveProjection = (lnglat: LngLatLike | undefined): Point | null => {
  // trigger a re-render when the viewState changes
  useViewState(true);
  const { mapRef } = useContext(KineticMapContext);
  if (!lnglat) {
    return null;
  }
  return mapRef.current?.project(lnglat) ?? null;
};

export const useKineticMap = () => {
  const { fitBounds, setZoom, mapRef, setViewState, setDragEnabled } =
    useContext(KineticMapContext);
  const emptyBounds = () => new LngLatBounds();
  const setViewStateCallback = useCallback(
    (viewState: SetStateAction<Partial<ViewState> & DeckTransitionProps>) => {
      return setViewState((prev) => {
        const newViewState = typeof viewState === 'function' ? viewState(prev) : viewState;
        return {
          transitionDuration: getDuration(
            prev.latitude!,
            prev.longitude!,
            newViewState.latitude!,
            newViewState.longitude!,
            prev.zoom!,
            newViewState.zoom!
          ),
          transitionInterpolator: new FlyToInterpolator(),
          ...newViewState,
        };
      });
    },
    [setViewState]
  );
  return useMemo(
    () => ({
      fitBounds,
      setZoom,
      setDragEnabled,
      mapRef,
      getBounds: mapRef.current?.getBounds ?? emptyBounds,
      setViewState: setViewStateCallback,
    }),
    [fitBounds, setZoom, setDragEnabled, mapRef, setViewStateCallback]
  );
};

function getDuration(
  prevLat: number,
  prevLng: number,
  lat: number,
  lng: number,
  prevZoom: number,
  zoom: number
) {
  if (!prevLat || !prevLng || !lat || !lng || !prevZoom || !zoom) {
    return DEFAULT_DURATION;
  }

  return getScaledDuration(prevLat, prevLng, lat, lng, prevZoom, zoom);
}
